export function getMimeTypeFromDataURL(dataURL: string) {
	return (dataURL.substring(dataURL.indexOf(':') + 1, dataURL.indexOf(';')));
};
export const measure = (() => {
	const measures = <Map<string, number>>new Map;
	return ((what: string, useLogger: boolean = false) => {
		let t0: number | undefined = measures.get(what);
		if (t0) {
			var t1 = performance.now();
			var dt = t1 - t0;
			measures.delete(what);
			if (useLogger !== false) {
				console.warn(`<measure name='${what}'>${dt}</measure>`);
			}
			return (dt);
		} else {
			t0 = performance.now();
			measures.set(what, t0);
			return (0);
		}
	});
})();
// Retrieve a data indexed by quantity
// used column is closest column with quantity < to current quantity
// Exemple:
// const data = {
// 	0  : 0,      1  : 1,
// 	2  : 12,     3  : 103,
// 	4  : 1004,   5  : 10005,
// 	10 : 100006, 15 : 1000007,
// 	20 : 10000008
// };
// console.log({
// 	0  : getDataInFunctionOfQuantity(data, 0),
// 	1  : getDataInFunctionOfQuantity(data, 1),
// 	2  : getDataInFunctionOfQuantity(data, 2),
// 	4  : getDataInFunctionOfQuantity(data, 4),
// 	8  : getDataInFunctionOfQuantity(data, 8),
// 	16 : getDataInFunctionOfQuantity(data, 16)
// });
// Should display
// {
// 	0  : 0,
// 	1  : 1,
// 	2  : 12,
// 	4  : 1004,
// 	8  : 10005,
// 	16 : 1000007
// }
export function getDataInFunctionOfQuantity<T>(
	store: Record<number, T>,
	quantity: number
) {
	try {
		const couples = (
			Object.entries(store).map(x => <[number, T]>[parseInt(x[0]), x[1]]).filter(x => x[0] <= quantity)
		);
		const first = <[number, T]>couples.shift();
		const min = couples.reduce((a, c) => {
			return ((a[0] > c[0]) ? a : c);
		}, first);
		return (min[1]);
	} catch (error: any) {
		throw E.bindAsBound('Unknown error!', {
			store,
			quantity
		}, error);
	}
};

/**
 * Mark a part of the code as unreachable and throw an error at runtime if that part is executed.
 **/
export function never(): never {
	throw new Error('Never!');
};

export function getDataInFunctionOfMinimalPositiveValue(store: Record<number, number>) {
	try {
		const couples = <[number, number][]>(
			Object.entries(store).
			map(x => [
				parseInt(x[0]),
				x[1]
			]).
			filter(x => x[1])
		);
		if (!couples.length) {
			throw E.user('No positive value found in the store!', {store});
		}
		const first = <[number, number]>couples.shift();
		const min = couples.reduce<[number, number]>((a, c) => {
			return a[0] < c[0] ? a : c;
		}, first);
		return {
			quantity: min[0],
			value: min[1]
		};
	} catch (error: any) {
		E.handle(error, {
			unknownError() {
				throw E.bindAsBound('Unknown error!', {store}, error);
			},
			unhandledError() {
				throw error;
			}
		});
		never();
	}
};


export function createPromisePack<T>() {
	let resolveReference: (result: T) => void = () => {
	};
	let rejectReference: (error: Error) => void = () => {
	};
	const promise = new Promise<T>((resolve, reject) => {
		resolveReference = resolve;
		rejectReference = reject;
	});
	return ({
		promise,
		resolve: resolveReference,
		reject: rejectReference
	});
};

/**
 * Call $fn each $frequencyInMs while it's return is not true
 * If $fn return true poll return true too, if timeout is reached return false
 **/
export async function poll(
	fn: () => (boolean | Promise<boolean>),
	frequencyInMs: number,
	timeoutInMs: number
) {
	const ppack = createPromisePack();
	const timeoutId = setTimeout(() => {
		clearInterval(intervalId);
		ppack.resolve(false);
	}, timeoutInMs);
	const intervalId = setInterval(async () => {
		try {
			if (await fn()) {
				clearTimeout(timeoutId);
				clearInterval(intervalId);
				ppack.resolve(true);
			}
		} catch (error: unknown) {
			E.userAssert(error instanceof Error, 'Error is not an error!', {
				error
			});
			ppack.reject(error);
		}
	}, frequencyInMs);
	return (await ppack.promise);
};

export interface IDebouncerDescriptor<T = void> {
	fn: (...params: any[]) => T;
	delay: number;
};

export interface IAsyncDebouncerDescriptor extends IDebouncerDescriptor<Promise<void>> {
};

export interface IAccuDebouncerDescriptor extends IDebouncerDescriptor {
	forEach?: (...params: any[]) => void;
};
export const createDebouncer = (() => {
	let debouncersId = 0;
	const timeoutIdByDebouncerId: {
		[key: string]: number | null;
	} = {};
	return (cfg: IDebouncerDescriptor) => {
		const debouncerId = debouncersId++;
		return (...params: any[]) => {
			const curTid = timeoutIdByDebouncerId[debouncerId];
			if (curTid) {
				clearTimeout(curTid);
			}
			const tid = setTimeout(() => {
				timeoutIdByDebouncerId[debouncerId] = null;
				cfg.fn.apply(null, params);
			}, cfg.delay) as any as number;
			timeoutIdByDebouncerId[debouncerId] = tid;
		};
	};
})();
export const createDebouncerPromise = (() => {
	let debouncersId = 0;
	const timeoutIdByDebouncerId: {
		[key: string]: number | null | true;
	} = {};
	return (cfg: IAsyncDebouncerDescriptor) => {
		const debouncerId = debouncersId++;
		const f = <T>(...params: any[]): Promise<T> => {
			const curTid = timeoutIdByDebouncerId[debouncerId];
			if (curTid) {
				if (curTid === true) {
					//An instance callback is already running, await termination
					return new Promise((resolve, reject) => {
						setTimeout(() => {
							//any because WTF
							f(...params).then(<any>resolve).catch(reject);
						}, cfg.delay);
					});
				} else {
					clearTimeout(curTid);
				}
			}
			return (new Promise((resolve, reject) => {
				timeoutIdByDebouncerId[debouncerId] = setTimeout(async () => {
					try {
						timeoutIdByDebouncerId[debouncerId] = true;
						const res = await cfg.fn.apply(null, params);
						timeoutIdByDebouncerId[debouncerId] = null;
						resolve(res as any);
					} catch (e) {
						timeoutIdByDebouncerId[debouncerId] = null;
						reject(e);
					}
				}, cfg.delay) as any as number;
			}));
		};
		return f;
	};
})();

export const createDebouncerAccumulator = (() => {
	let debouncersId = 0;
	const timeoutIdByDebouncerId: {
		[key: string]: number | null;
	} = {};
	return (cfg: IAccuDebouncerDescriptor) => {
		const debouncerId = debouncersId++;
		const accu: any[] = [];
		const forEach = cfg.forEach || (() => void 1);
		return (...params: any[]) => {
			accu.push(params);
			forEach(...params);
			const curTid = timeoutIdByDebouncerId[debouncerId];
			if (curTid) {
				clearTimeout(curTid);
			}
			const tid = setTimeout(() => {
				timeoutIdByDebouncerId[debouncerId] = null;
				const params = Array.from(accu);
				accu.length = 0;
				cfg.fn.call(null, params);
			}, cfg.delay) as any as number;
			timeoutIdByDebouncerId[debouncerId] = tid;
		};
	};
})();

import * as E from './errors';
